import React, {
    type AriaAttributes,
    type ButtonHTMLAttributes,
    type ComponentPropsWithRef,
} from 'react';
import {
    type FocusRingAria,
    mergeProps,
    type PressHookProps,
    type PressResult,
    useFocusRing,
    usePress,
} from 'react-aria';
import { ButtonContext, useContextProps } from 'react-aria-components';

import { assertNever } from '@gitbook/util-ts';

import { type NavigationHookProps, type NavigationResult, useNavigationProps } from '../Router';

export type GenericButtonHookProps = Pick<
    PressHookProps,
    'preventFocusOnPress' | 'onPress' | 'onPressStart'
> & {
    disabled?: boolean;
};

export type ButtonHookProps = GenericButtonHookProps & {
    elementType: 'button' | 'a' | 'div' | 'label';
    type?: ButtonHTMLAttributes<HTMLButtonElement>['type'];
};

export type ButtonResult = {
    buttonProps: PressResult['pressProps'] &
        FocusRingAria['focusProps'] & {
            disabled?: boolean;
            'aria-disabled'?: AriaAttributes['aria-disabled'];
        };
    isPressed: boolean;
    isFocusVisible: boolean;
    isDisabled: boolean;
};

/**
 * Low-level hook to create a button component.
 */
export function useButton(props: ButtonHookProps): ButtonResult {
    const { disabled, onPress, onPressStart, preventFocusOnPress, elementType } = props;

    const { pressProps, isPressed } = usePress({
        onPress,
        onPressStart,
        isDisabled: disabled,
        preventFocusOnPress,
    });

    const { focusProps, isFocusVisible } = useFocusRing({
        within: elementType === 'label',
    });

    const getElementProps = () => {
        switch (elementType) {
            case 'button':
                return {
                    disabled,
                    'data-pressed': isPressed,
                    type: props.type || 'button',
                };
            case 'a':
                return { 'aria-disabled': disabled || undefined, 'data-pressed': isPressed };
            case 'div':
                return {
                    role: 'button',
                    'aria-disabled': disabled || undefined,
                    'data-pressed': isPressed,
                    tabIndex: disabled ? undefined : 0,
                };
            case 'label': {
                return {};
            }
            default:
                assertNever(elementType);
        }
    };

    return {
        buttonProps: mergeProps(getElementProps(), pressProps, focusProps, {
            'data-focus-visible': isFocusVisible || undefined,
        }),
        isPressed,
        isFocusVisible,
        isDisabled: Boolean(disabled),
    };
}

export type LinkHookProps = GenericButtonHookProps & NavigationHookProps;

export type LinkResult = Omit<ButtonResult, 'buttonProps'> & {
    linkProps: ButtonResult['buttonProps'] & NavigationResult;
};

/**
 * Low-level hook to create a link component.
 */
export function useLink(props: LinkHookProps): LinkResult {
    const navigationProps = useNavigationProps(props);
    const button = useButton({ ...props, elementType: 'a' });
    return {
        ...button,
        linkProps: mergeProps(navigationProps, button.buttonProps),
    };
}

export type ButtonStyleHookProps = {
    isPressed: boolean;
    isFocusVisible: boolean;
    className?: string;
};

export type ButtonStyleResult = { className: string };

export type AtomButtonProps = ComponentPropsWithRef<'button'> & GenericButtonHookProps;

export function AtomButton({ ref, ...props }: AtomButtonProps) {
    [props, ref] = useContextProps(props, ref, ButtonContext);
    const { children, disabled, onPress, onPressStart, preventFocusOnPress, ...rest } = props;

    const { buttonProps } = useButton({
        disabled,
        onPress,
        onPressStart,
        preventFocusOnPress,
        elementType: 'button',
        type: rest.type,
    });

    return (
        <button ref={ref} {...mergeProps(rest, buttonProps)}>
            {children}
        </button>
    );
}

export type AtomLinkProps = Omit<ComponentPropsWithRef<'a'>, 'href'> &
    Omit<LinkHookProps, 'elementType'>;

export function AtomLink({ ref, ...props }: AtomLinkProps) {
    const {
        children,
        disabled,
        href,
        onClick,
        onPress,
        onPressStart,
        preventFocusOnPress,
        routerOptions,
        ...rest
    } = props;

    const { linkProps } = useLink({
        disabled,
        href,
        onClick,
        onPress,
        onPressStart,
        preventFocusOnPress,
        routerOptions,
        download: rest.download,
        target: rest.target,
    });

    return (
        <a ref={ref} {...mergeProps(rest, linkProps)}>
            {children}
        </a>
    );
}
